import {
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentReturnedOutlinedIcon from '@mui/icons-material/AssignmentReturnedOutlined';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import { showLabel } from '@/utils/helper';
import { OnoffType } from '@/api/enum/common.enum';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { DefaultValues, useForm } from 'react-hook-form';
import {
	useBatchUpdateSingleMeditationHasNewMutation,
	useGetSingleStatusListQuery,
} from '@/api/exercise/meditationApiSlice';
import { DataGrid } from '../MeditationListSummaryPage';
import { UnitSeriesSinglePageResDTO } from '@/api/DTO/exercise/meditation.interface';
import { useParams } from 'react-router-dom';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';

interface Props {}

export default function SetSingleNewDialog(props: Props) {
	const showModal = useModal();
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const [batchUpdateSingleMeditationHasNew] =
		useBatchUpdateSingleMeditationHasNewMutation();
	const { meditationId } = useParams();

	const onoffList = [
		{
			id: OnoffType.off,
			name: t('information.off'),
		},
		{
			id: OnoffType.on,
			name: t('information.on'),
		},
	];

	const canList = [
		{ id: true, name: t('meditation.can') },
		{ id: false, name: t('meditation.cannot') },
	];
	const yesOrNoList = [
		{ id: true, name: t('meditation.yes') },
		{ id: false, name: t('meditation.no') },
	];
	const exerciseTypeList = [
		{ id: 1, name: t('meditation.audio') },
		{ id: 2, name: t('meditation.video') },
	];

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetSingleStatusListQuery(meditationId || '', {
			refetchOnMountOrArgChange: true,
			skip: !open,
		});

	const defaultValues: DefaultValues<any> = {};

	const methods = useForm<any>({
		defaultValues,
	});

	const onSubmit = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('meditation.setNewExercise')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('meditation.setNewExerciseTips')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const data: any[] = Object.entries(methods.getValues()).map(
						([key, value]) => {
							return {
								singleId: key,
								hasNew: value,
							};
						}
					);
					const response = await batchUpdateSingleMeditationHasNew({
						list: data,
						id: meditationId || '',
					});
					if ('data' in response) {
						enqueueSnackbar(t('meditation.setNewExerciseSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						handleClose();
					}
				}}
			/>
		));
	};

	const handleClose = () => {
		setOpen(false);
	};

	const columns: GridColDef[] = [
		{
			field: 'singleId',
			headerName: t('meditation.exerciseTag'),
			flex: 25,
			renderCell: (params) => {
				const arr = (params.row.tags || []).map(
					(item: { tagNameZh: any }) => item.tagNameZh
				);
				return arr.join(',');
			},
		},
		{
			field: 'titleZh',
			headerName: t('meditation.exerciseTitleZh'),
			flex: 25,
		},
		{
			field: 'mediaType',
			headerName: t('meditation.exerciseType'),
			flex: 15,
			renderCell: (params) => (
				<>{showLabel(params.row.mediaType, exerciseTypeList)}</>
			),
		},
		{
			field: 'status',
			headerName: t('meditation.exerciseStatus'),
			flex: 15,
			renderCell: (params: any) => (
				<Chip
					color={params.row.status == OnoffType.on ? 'chipPrimary' : 'default'}
					label={showLabel(params.row.status, onoffList)}
				/>
			),
		},
		{
			field: 'changePublished',
			headerName: t('meditation.setNewExerciseItem'),
			flex: 20,
			renderCell: (params: any) => (
				<AppRadioGroup
					control={methods.control}
					name={params.row.singleId}
					row
					options={yesOrNoList}
				/>
			),
		},
	];

	const transformResponse = useCallback(
		(response?: UnitSeriesSinglePageResDTO[]) => {
			if (!response) return;
			const newResponse: DataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
				});

				methods.setValue(item.singleId, item.hasNew ? true : false);
			});

			return newResponse;
		},
		[]
	);

	const formContent = (
		<>
			<Button
				variant='contained'
				style={{ alignItems: 'center' }}
				endIcon={<AssignmentReturnedOutlinedIcon />}
				onClick={() => setOpen(true)}
			>
				{t('meditation.setNewExercise')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('meditation.setNewExercise')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Grid container alignItems='stretch' columnSpacing={3} marginTop={3}>
						<Grid item xs display='flex'>
							<LocalDataGrid
								rows={transformResponse(propertySummary?.data) ?? []}
								columns={columns}
								rowCount={0}
								getRowId={(row) => row.singleId}
								pagination={false}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('button.save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
